import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Button,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import { useBooking } from "../context/BookingContext";

const activeBike = {
  name: "Blaze",
  image: "/assets/images/high-speed-1.png",
  tag: "Fast lane · everyday city runs",
  speed: "65",
  range: "120",
  capacity: "150",
  daily: "349",
  weekly: "1,999",
};

function StatBlock({ value, unit, label, dark }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="baseline" spacing={0.4}>
        <Typography
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 26,
            color: dark ? "#FFFFFF" : "text.primary",
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: 12.5,
            color: dark ? "rgba(255,255,255,0.55)" : "text.secondary",
          }}
        >
          {unit}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: dark ? "rgba(255,255,255,0.4)" : "text.secondary",
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function AvailableCard() {
  const { openBookingModal } = useBooking();

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: "28px",
        overflow: "hidden",
        bgcolor: "#ffffff",
        border: "1px solid #e8edf5",
        boxShadow: "0 24px 56px -16px rgba(27,78,216,0.14), 0 4px 16px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 36px 64px -16px rgba(27,78,216,0.22), 0 6px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(90deg, #1B4ED8 0%, #72BF20 100%)",
        }}
      />

      {/* Image Stage */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 240, md: 270 },
          background: "linear-gradient(160deg, #eef2ff 0%, #dbeafe 60%, #e0f2fe 100%)",
          overflow: "hidden",
        }}
      >
        {/* Subtle background shape */}
        <Box
          sx={{
            position: "absolute",
            bottom: -40,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(27,78,216,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          component="img"
          src={activeBike.image}
          alt={activeBike.name}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            p: { xs: 2, md: 3 },
            filter: "drop-shadow(0 16px 28px rgba(27,78,216,0.18))",
          }}
        />

        {/* Gradient fade at bottom */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            background: "linear-gradient(180deg, transparent 0%, #f5f8ff 100%)",
          }}
        />

        {/* AVAILABLE NOW badge */}
        <Box
          sx={{
            position: "absolute",
            top: 18,
            left: 18,
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.4,
            py: 0.55,
            borderRadius: "999px",
            bgcolor: "#72BF20",
            boxShadow: "0 4px 14px rgba(114,191,32,0.38)",
          }}
        >
          <BoltIcon sx={{ fontSize: 14, color: "#fff" }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.04em" }}>
            AVAILABLE NOW
          </Typography>
        </Box>

        {/* Price pill */}
        <Box
          sx={{
            position: "absolute",
            top: 18,
            right: 18,
            px: 1.4,
            py: 0.55,
            borderRadius: "999px",
            bgcolor: "rgba(27,78,216,0.08)",
            border: "1px solid rgba(27,78,216,0.15)",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1B4ED8" }}>
            From ₹{activeBike.weekly}
            <Box component="span" sx={{ color: "#64748b", fontWeight: 500 }}>/wk</Box>
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          px: { xs: 3, md: 3.5 },
          pb: { xs: 3, md: 3.5 },
          pt: 2.5,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600, mb: 0.5, letterSpacing: "0.02em" }}>
          {activeBike.tag}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 26,
            color: "#0f172a",
            mb: 2.5,
            letterSpacing: "-0.02em",
          }}
        >
          {activeBike.name}
        </Typography>

        {/* Stats */}
        <Stack
          direction="row"
          spacing={0}
          sx={{
            mb: 2.5,
            pb: 2.5,
            borderBottom: "1px solid #e8edf5",
          }}
        >
          {[
            { value: activeBike.speed, unit: "km/h", label: "Top Speed" },
            { value: activeBike.range, unit: "km", label: "Range" },
            { value: activeBike.capacity, unit: "kg", label: "Capacity" },
          ].map((stat, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                textAlign: "center",
                position: "relative",
                "&:not(:last-child)::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "10%",
                  height: "80%",
                  width: "1px",
                  bgcolor: "#e8edf5",
                },
              }}
            >
              <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.3}>
                <Typography
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 800,
                    fontSize: 24,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>
                  {stat.unit}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  mt: 0.3,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Pricing tiles */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
          <Box
            sx={{
              flex: 1,
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              border: "1px solid #e8edf5",
              px: 2,
              py: 1.4,
            }}
          >
            <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.05em", mb: 0.4 }}>
              DAILY RATE
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: 19,
                color: "#0f172a",
              }}
            >
              ₹{activeBike.daily}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: "14px",
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              px: 2,
              py: 1.4,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: "#16a34a",
                fontWeight: 700,
                letterSpacing: "0.05em",
                mb: 0.4,
              }}
            >
              WEEKLY RATE
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: 19,
                color: "#0f172a",
              }}
            >
              ₹{activeBike.weekly}
            </Typography>
          </Box>
        </Stack>

        {/* Book button */}
        <Button
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardIcon />}
          onClick={() => openBookingModal("Blaze")}
          sx={{
            mt: "auto",
            background: "linear-gradient(135deg, #1B4ED8 0%, #2563eb 100%)",
            color: "#fff",
            borderRadius: "14px",
            py: 1.4,
            fontWeight: 700,
            fontSize: 15,
            fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: "-0.01em",
            boxShadow: "0 8px 24px rgba(27,78,216,0.30)",
            "&:hover": {
              background: "linear-gradient(135deg, #1e40af 0%, #1B4ED8 100%)",
              boxShadow: "0 12px 32px rgba(27,78,216,0.42)",
            },
          }}
        >
          Book {activeBike.name}
        </Button>
      </Box>
    </Box>
  );
}

function ComingSoonCard() {
  const { openBookingModal } = useBooking();

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: "28px",
        overflow: "hidden",
        bgcolor: "#FFFFFF",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        boxShadow: "0 20px 40px -24px rgba(20,23,26,0.18)",
        "&:hover": { transform: "translateY(-6px)" },
      }}
    >
      {/* Image Stage */}
      <Box sx={{ position: "relative", height: { xs: 240, md: 270 }, bgcolor: "#EDEFF1" }}>
        <Box
          component="img"
          src="/assets/images/fleet-scooter-1.jpg"
          alt="Lite Series — coming soon"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "grayscale(1) opacity(0.32) blur(1px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 70,
            background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 100%)",
          }}
        />

        {/* Status Ribbon */}
        <Box
          sx={{
            position: "absolute",
            top: 18,
            left: 18,
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.5,
            py: 0.6,
            borderRadius: "999px",
            bgcolor: "rgba(20,23,26,0.85)",
            backdropFilter: "blur(8px)",
          }}
        >
          <LockClockRoundedIcon sx={{ fontSize: 15, color: "#fff" }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#fff", letterSpacing: "0.03em" }}>
            COMING SOON
          </Typography>
        </Box>

        {/* Diagonal Stamp */}
        <Box
          sx={{
            position: "absolute",
            top: 26,
            right: -34,
            width: 150,
            textAlign: "center",
            py: 0.5,
            bgcolor: "rgba(90, 98, 106, 0.92)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            transform: "rotate(38deg)",
            boxShadow: "0 6px 14px rgba(20,23,26,0.25)",
          }}
        >
          SOON
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SpeedIcon sx={{ fontSize: 56, color: "rgba(20,23,26,0.14)" }} />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 3, md: 3.5 }, pb: { xs: 3, md: 3.5 }, pt: 1, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 600, mb: 0.5 }}>
          For everyday · city commutes
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 27,
            color: "text.primary",
            mb: 2,
            letterSpacing: "-0.01em",
          }}
        >
          Lite Series
        </Typography>

        <Stack direction="row" spacing={3} sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <StatBlock value="45" unit="km/h" label="Top speed" />
          <StatBlock value="90" unit="km" label="Range" />
          <StatBlock value="150" unit="kg" label="Capacity" />
        </Stack>

        <Box
          sx={{
            mb: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "14px",
            border: "1.5px dashed",
            borderColor: "divider",
            bgcolor: "background.default",
            py: 1.9,
            px: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 600, textAlign: "center" }}>
            Pricing drops closer to launch
          </Typography>
        </Box>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<NotificationsActiveRoundedIcon />}
          onClick={() => openBookingModal("Blaze")}
          sx={{
            mt: "auto",
            borderRadius: "12px",
            py: 1.3,
            fontWeight: 700,
            fontSize: 15.5,
            borderWidth: "1.5px",
            borderColor: "text.primary",
            color: "text.primary",
            "&:hover": {
              borderWidth: "1.5px",
              borderColor: "primary.main",
              color: "primary.main",
              bgcolor: "rgba(27, 78, 216, 0.04)",
            },
          }}
        >
          Notify Me at Launch
        </Button>
      </Box>
    </Box>
  );
}

export default function Fleet() {
  return (
    <Box id="fleet" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "background.default" }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, maxWidth: 620 }}>
          <Box
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.6,
              borderRadius: "20px",
              bgcolor: "rgba(27, 78, 216, 0.08)",
              color: "primary.main",
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            OUR FLEET
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: { xs: 30, md: 38 },
              fontWeight: 700,
              color: "text.primary",
              mb: 2,
              letterSpacing: "-0.02em",
            }}
          >
            High Performance.{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              Premium Mobility.
            </Box>
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 16, lineHeight: 1.7 }}>
            Pick Blaze for ready-to-book power, or register interest for our upcoming Lite series arriving soon.
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <AvailableCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <ComingSoonCard />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}